import moment from 'moment-timezone';
import { HistoryDetailFlightInfo } from './HistoryDetailFlightInfo';

/**
 * Converts a zoneless local datetime (e.g. "2026-05-26T22:30:00") into the
 * absolute UTC instant it represents within the given IANA timezone.
 *
 * The pickup time from the API carries no offset, so it must be interpreted in
 * the booking's own timezone rather than the viewer's local time. If the
 * timezone is missing or unknown, we fall back to parsing it as-is.
 *
 * @param {string} localDateTime - Zoneless local datetime string.
 * @param {string} [timezone] - IANA timezone name (e.g. "America/New_York").
 * @returns {Date} The corresponding UTC Date instant.
 */
function toUtcInstant(localDateTime, timezone) {
  const parsed = timezone && moment.tz.zone(timezone)
    ? moment.tz(localDateTime, timezone)
    : moment(localDateTime);
  return parsed.toDate();
}

/**
 * @typedef {Object} BookingHistoryLocation
 * @property {string} address
 * @property {number} latitude
 * @property {number} longitude
 * @property {string[]} locationTypes
 * @property {string} schemaVersion
 * @property {string | null} dispatchAddressId
 */

/**
 * @typedef {Object} BookingHistoryUserInfo
 * @property {string} [givenName]
 * @property {string} [familyName]
 * @property {string} [phone]
 * @property {string} [email]
 */

/**
 * @typedef {Object} BookingHistoryPayment
 * @property {string} type
 * @property {string} [businessAccountId]
 * @property {string} [paymentMethodId]
 * @property {string} [paymentProviderName]
 */

/**
 * @typedef {Object} BookingHistoryOfferProperties
 * @property {number} [seats]
 * @property {number} [luggage]
 * @property {string} [companyName]
 * @property {string} [vehicleType]
 * @property {string} [vehicleAlias]
 * @property {string} [dispatchVehicleTitle]
 */

/**
 * @typedef {Object} BookingHistoryOffer
 * @property {string} id
 * @property {string} name
 * @property {string} peId
 * @property {string} fleetId
 * @property {string} fleetType
 * @property {string} oldOfferId
 * @property {BookingHistoryOfferProperties} properties
 * @property {string} fleetPriority
 * @property {string} schemaVersion
 * @property {boolean} isOfferFromNetwork
 * @property {boolean} isBookingFeeApplied
 * @property {*} attributeExtras
 */

/**
 * @typedef {Object} BookingHistoryPrematureCancellationFee
 * @property {string} schemaVersion
 * @property {number | null} cancellationFee
 * @property {number | null} exemptDriverAssignmentDurationSec
 * @property {number | null} chargeablePostDriverAssignedDurationSec
 */

/**
 * @typedef {Object} BookingHistoryBookedByUserInfo
 * @property {string} id
 * @property {string} [email]
 * @property {string} [givenName]
 * @property {string} [familyName]
 * @property {string} [type]
 */

/**
 * @typedef {Object} BookingHistoryWaitingTime
 * @property {string} type
 * @property {number | null} value
 */

/**
 * @typedef {Object} BookingHistoryDetailConstructorArgs
 * @property {number} id
 * @property {string} userId
 * @property {string | null} [passengerId]
 * @property {string} bookingId
 * @property {string} bookingChannelId
 * @property {string} corporationId
 * @property {string} companyName
 * @property {string | null} [dispatchId]
 * @property {string} status
 * @property {ISO_TIMESTAMP} createdAt
 * @property {string} timezone
 * @property {BookingHistoryLocation} origin
 * @property {BookingHistoryLocation} destination
 * @property {BookingHistoryLocation[]} waypoints
 * @property {BookingHistoryUserInfo} userInfo
 * @property {LOCAL_DATETIME} pickupTime
 * @property {boolean} requiresConfirmation
 * @property {BookingHistoryPayment} payment
 * @property {string} fleetId
 * @property {BookingHistoryOffer} offer
 * @property {string} passengerAccountType
 * @property {BookingHistoryPrematureCancellationFee | null} [prematureCancellationFee]
 * @property {string} [bookingSource]
 * @property {BookingHistoryBookedByUserInfo | null} [bookedByUserInfo]
 * @property {number | null} [distanceMeters]
 * @property {BookingHistoryWaitingTime | null} [waitingTime]
 * @property {string} serviceType
 * @property {{ flightInfo?: HistoryDetailFlightInfoArgs | null } | null} [bookingProperties]
 */

export class BookingHistoryDetail {
  /**
   * @param {BookingHistoryDetailConstructorArgs} args
   */
  constructor(args) {
    /** @type {number} */
    this.id = args.id;
    /** @type {string} */
    this.userId = args.userId;
    /** @type {string | null} */
    this.passengerId = args.passengerId ?? null;
    /** @type {string} */
    this.bookingId = args.bookingId;
    /** @type {string} */
    this.bookingChannelId = args.bookingChannelId;
    /** @type {string} */
    this.corporationId = args.corporationId;
    /** @type {string} */
    this.companyName = args.companyName;
    /** @type {string | null} */
    this.dispatchId = args.dispatchId ?? null;
    /** @type {string} */
    this.status = args.status;
    /** @type {string} */
    this.createdAt = args.createdAt;
    /** @type {string} */
    this.timezone = args.timezone;
    /** @type {BookingHistoryLocation} */
    this.origin = args.origin;
    /** @type {BookingHistoryLocation} */
    this.destination = args.destination;
    /** @type {BookingHistoryLocation[]} */
    this.waypoints = args.waypoints ?? [];
    /** @type {BookingHistoryUserInfo} */
    this.userInfo = args.userInfo;
    /** @type {Date} */
    this.pickupTime = toUtcInstant(args.pickupTime, args.timezone);
    /** @type {boolean} */
    this.requiresConfirmation = args.requiresConfirmation;
    /** @type {BookingHistoryPayment} */
    this.payment = args.payment;
    /** @type {string} */
    this.fleetId = args.fleetId;
    /** @type {BookingHistoryOffer} */
    this.offer = args.offer;
    /** @type {string} */
    this.passengerAccountType = args.passengerAccountType;
    /** @type {BookingHistoryPrematureCancellationFee | null} */
    this.prematureCancellationFee = args.prematureCancellationFee ?? null;
    /** @type {string | null} */
    this.bookingSource = args.bookingSource ?? null;
    /** @type {BookingHistoryBookedByUserInfo | null} */
    this.bookedByUserInfo = args.bookedByUserInfo ?? null;
    /** @type {number | null} */
    this.distanceMeters = args.distanceMeters ?? null;
    /** @type {BookingHistoryWaitingTime | null} */
    this.waitingTime = args.waitingTime ?? null;
    /** @type {string} */
    this.serviceType = args.serviceType;
    /**
     * Always present, even when the booking has no associated flight — ask
     * {@link HistoryDetailFlightInfo#hasFlightInfo} to know whether any flight
     * information is available.
     * @type {HistoryDetailFlightInfo}
     */
    this.flightInfo = HistoryDetailFlightInfo.fromApiDto(args.bookingProperties?.flightInfo);
  }

  /**
   * @returns {string}
   */
  get fullName() {
    return [this.userInfo.givenName, this.userInfo.familyName].filter(Boolean).join(' ').trim();
  }

  /**
   * @param {BookingHistoryDetailApiDto} dto
   * @returns {BookingHistoryDetail}
   */
  static fromApiResponse(dto) {
    return new BookingHistoryDetail(dto);
  }

  /**
   *
   * @return {string}
   */
  getFormattedCurrentTimeAtTimezone() {
    return moment.tz(this.timezone).format('YYYY-MM-DDTHH:mm:ss');
  }

  /**
   * Formats the stored UTC pickup instant as the wall-clock time in the
   * booking's timezone (e.g. "2026-05-26T22:30:00" for America/New_York),
   * recovering the original zoneless local datetime from the API.
   *
   * @return {LOCAL_DATETIME}
   */
  getFormattedLocalPickupTimeAtTimezone() {
    const format = 'YYYY-MM-DDTHH:mm:ss';
    return moment(this.pickupTime).tz(this.timezone).format(format);
  }

  /**
   * Returns 0 if pickup time is in the past
   * @returns {number}
   */
  getSecondsUntilPickupTime() {
    const now = new Date();
    const diff = this.pickupTime.getTime() - now.getTime();
    return Math.max(0, Math.floor(diff / 1000));
  }

  isBookingForBusinessPassenger() {
    return this.passengerAccountType === 'corporationEmployee';
  }

  isBookingForBusinessGuest() {
    return this.passengerAccountType === 'corporationGuest';
  }

  getBusinessGuestId() {
    return this.userId;
  }

  getBusinessPassengerId() {
    return this.userId;
  }

  getOriginAddressSource() {
    if (this.origin.dispatchAddressId) {
      return 'icabbi';
    }

    return 'google';
  }

  getDestinationAddressSource() {
    if (this.destination.dispatchAddressId) {
      return 'icabbi';
    }

    return 'google';
  }

  getWaypointAddressSource(waypoint) {
    return waypoint.dispatchAddressId ? 'icabbi' : 'google';
  }
}
